import React from "react";
import SplashNavBar from "../components/SplashNavBar";
import { Grid, Box, Typography, Container, Button } from "@mui/material";

//
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";

const SplashPage: React.FC = () => {
  const handleGetStarted = () => {
    window.location.href = "/signup";
  };

  const handleLearnMore = () => {
    const contentSection = document.getElementById("content-section");
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      <SplashNavBar />
      <Box sx={{ marginTop: "64px" }}>
        <Box
          sx={{
            backgroundImage: `url('/images/background-image.jpg')`,
            backgroundAttachment: "fixed",
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "100vh",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)", // Adjust darkness here
              zIndex: 1,
            }}
          />
          {/* Text Content */}
          <Container
            sx={{
              position: "relative",
              zIndex: 2,
              color: "white",
              textAlign: "left",
              padding: "4em 2em",
              maxWidth: "800px",
            }}
          >
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                color: "#121212", // Use a professional dark font color
                textShadow: "1px 1px 2px #fff", // Add a subtle shadow for contrast
                fontWeight: "bold",
                textAlign: "right",
              }}
            >
              Start preparing for the AT-SA today.
            </Typography>
            <Typography
              variant="h5"
              sx={{
                textAlign: "right",
                fontStyle: "italic",
                fontWeight: "bold",
              }}
            >
              The AT-SA is ranked against your peers, so compete with them while
              you prepare.
            </Typography>
            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",

                marginTop: "2em",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleGetStarted}
                sx={{
                  fontWeight: "bold",
                  padding: "0.8em 1.5em",
                  fontFamily: "Roboto Slab",
                }}
              >
                GET STARTED
              </Button>
              <Button
                variant="outlined"
                onClick={handleLearnMore}
                sx={{
                  fontWeight: "bold",
                  padding: "0.8em 1.5em",
                  color: "#121212",
                  borderColor: "#121212",
                }}
              >
                Learn More
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Content-Heavy Section */}
      <Box
        id="content-section"
        sx={{
          backgroundColor: "background.default",
          color: "text.primary",
          padding: "4em 2em",
        }}
      >
        <Container sx={{ maxWidth: "1000px" }}>
          <Typography variant="h4" gutterBottom>
            We created birdwatch club as a free resource for AT-SA Preparation
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            We created this platform to give aspiring air traffic controllers
            free, modern prep tools for the AT-SA. Whether or not you use other
            resources, we think being able to measure yourself against your
            peers is one of the most powerful ways to improve.
          </Typography>

          {/* Feature Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              {
                title: "Collision Simulator",
                description:
                  "Practice the most important section of the AT-SA with realistic multitasking simulations.",
              },
              {
                title: "Memory Challenges",
                description:
                  "Build fluency with numbers, variables, and mental tracking — just like on test day.",
              },
              {
                title: "Growing Resource Hub",
                description:
                  "Access new test sections and study tools as we expand our free prep materials.",
              },
            ].map((feature, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: "12px",
                    padding: "1.5em",
                    height: "100%",
                    transition: "0.3s",
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2">{feature.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Discord Callout Card */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "12px",
              padding: "2em",
              backgroundColor: "primary.main",

              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Questions or feedback?
            </Typography>
            <Typography variant="body2" paragraph>
              Join the <strong>ATC Hiring & Academy</strong> Discord group.
              We're not affiliated, but our resource is linked in the FAQs. I'm
              happy to answer questions — you can find me there as{" "}
              <strong>mbx.169</strong>.
            </Typography>
            <Button
              href="https://discord.gg/ejgFvs6k"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="medium"
              sx={{
                fontWeight: "bold",
                borderColor: "#5865F2",
                color: "#fff",
                backgroundColor: "#5865F2",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "#5865F2",
                  color: "#fff",
                },
              }}
            >
              Join the Discord
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Additional Section */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: `url('/images/background-image.jpg')`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          py: 8,
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Adjust for brightness
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Container
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "left",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Our platform is designed to help you excel
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                Interactive practice tests tailored to mimic the actual exam.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Detailed feedback and analytics to track your progress.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Expert guidance and tips to maximize your score.
              </Typography>
            </li>
          </ul>
        </Container>
      </Box>
      {/* AT-SA Overview Section */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          color: "text.primary",
          padding: "4em 2em",
        }}
      >
        <Container sx={{ maxWidth: "1000px" }}>
          <Typography variant="h4" gutterBottom>
            About the AT-SA Test
          </Typography>

          <Typography variant="body1" paragraph>
            The Air Traffic Skills Assessment (AT-SA) is a required aptitude
            test for aspiring air traffic controllers. While it's officially
            pass/fail, your score is ranked into bands—higher bands mean a
            better shot at getting hired.
          </Typography>

          <Grid container spacing={3} sx={{ marginTop: "2em" }}>
            {/* Section Cards */}
            {[
              {
                title: "Memory Tests",
                description:
                  "Recall numbers and variables quickly and accurately.",
              },
              {
                title: "Spatial Awareness",
                description:
                  "Determine aircraft positions from various perspectives.",
              },
              {
                title: "ATC Simulation",
                description: "Prevent collisions while juggling mental math.",
              },
              {
                title: "Word & Reading",
                description:
                  "Solve logic puzzles and comprehend short passages.",
              },
              {
                title: "Personality Inventory",
                description: "Choose responses that reflect your work style.",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: "12px",
                    padding: "1.5em",
                    height: "100%",
                    transition: "0.3s",
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography variant="body2">{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h5" gutterBottom sx={{ mt: 6 }}>
            Fast Facts
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                label: "Time",
                value: "2 hours 49 minutes (plus optional breaks)",
                icon: <AccessTimeIcon fontSize="large" />,
              },
              {
                label: "Cost",
                value: "Free (FAA covers it)",
                icon: <AttachMoneyIcon fontSize="large" />,
              },
              {
                label: "Results",
                value: "You’re placed in a hiring band, not given a score",
                icon: <AssessmentIcon fontSize="large" />,
              },
              {
                label: "Pro Tip",
                value: "Don’t overdo the math — avoid collisions first",
                icon: <TipsAndUpdatesIcon fontSize="large" />,
              },
            ].map((fact, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    borderRadius: "12px",
                    padding: "2em 1em",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "100%", // forces equal height
                    textAlign: "center",
                    boxShadow: 2,
                  }}
                >
                  <Box sx={{ mb: 1 }}>{fact.icon}</Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {fact.label}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {fact.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </div>
  );
};

export default SplashPage;
